/**
 * Embed Widget Editor Page
 * Configuration UI for the embed widget that can be used on external domains
 * Requires authentication using the same login as the embed widget
 */

import React, { useCallback } from 'react';

import { EmbedConfigPanel, THEME_PRESETS } from '../components/EmbedConfigPanel';
import { debugLog } from '../debug';
import { Brain, useEmbedEditor } from '../hooks/useEmbedEditor';
import { EmbedLoginPage } from '../pages/EmbedLoginPage';
import { useEmbedAuth } from '../providers/EmbedAuthProvider';
import { useEmbedConfig } from '../providers/EmbedConfigProvider';
import { useEmbedTheme } from '../theme';

// ============================================================================
// API Functions
// ============================================================================

async function fetchUserBrainsApi(appBaseUrl: string, token: string): Promise<Brain[]> {
  try {
    // Use 'asset' service - cognition APIs are on asset-api, not regular api
    const url = `${appBaseUrl}/api/widget-proxy/asset/cognition/list?limit=50`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Bolder-Widget': 'true',
      },
    });

    if (!response.ok) {
      console.error('[EmbedEditor] Failed to fetch brains:', response.status);
      return [];
    }

    const result = await response.json();
    // Handle various response formats:
    // - Direct array: [...]
    // - { cognitions: [...] }
    // - { data: [...] }
    // - { data: { cognitions: [...] } }  <-- This is what the API returns
    let cognitions: any[] = [];
    if (Array.isArray(result)) {
      cognitions = result;
    } else if (Array.isArray(result?.cognitions)) {
      cognitions = result.cognitions;
    } else if (Array.isArray(result?.data?.cognitions)) {
      cognitions = result.data.cognitions;
    } else if (Array.isArray(result?.data)) {
      cognitions = result.data;
    }

    if (!Array.isArray(cognitions) || cognitions.length === 0) {
      console.warn('[EmbedEditor] No cognitions found in response:', result);
      return [];
    }

    debugLog('[EmbedEditor] Found', cognitions.length, 'cognitions');

    return cognitions.map((c: any) => ({
      _id: c._id || c.id,
      name: c.name || c.Name || 'Unnamed Brain',
      displayImage: c.displayImage || c.DisplayImage,
    }));
  } catch (error) {
    console.error('[EmbedEditor] Error fetching brains:', error);
    return [];
  }
}

// ============================================================================
// Main Component
// ============================================================================

export const EmbedWidgetEditorPage: React.FC = () => {
  const { appBaseUrl, mediaBaseUrl, assetApiUrl } = useEmbedConfig();
  const { theme, primaryColor } = useEmbedTheme();
  const { isAuthenticated, jwt, user, logout, fetchProfile } = useEmbedAuth();

  // Local state for handle (fallback if GraphQL fails)
  const [localHandle, setLocalHandle] = React.useState<string>('');

  // Debug logging
  debugLog('[EmbedWidgetEditorPage] Auth state:', {
    isAuthenticated,
    hasJwt: !!jwt,
    userId: user?.id,
    userHandle: user?.handle,
    localHandle,
    appBaseUrl,
  });

  // Fetch profile to get user handle - try GraphQL first, then REST fallback
  React.useEffect(() => {
    if (!isAuthenticated || !user?.id || !jwt || !appBaseUrl) return;
    if (user?.handle || localHandle) return; // Already have handle

    const fetchHandle = async () => {
      // First try GraphQL (via fetchProfile)
      try {
        await fetchProfile();
      } catch (e) {
        debugLog('[EmbedWidgetEditorPage] GraphQL profile fetch failed, trying REST');
      }

      // Try REST API fallback
      try {
        const url = `${appBaseUrl}/api/widget-proxy/api/profile/handle/${user.id}`;
        debugLog('[EmbedWidgetEditorPage] Fetching handle from REST:', url);
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
            'X-Bolder-Widget': 'true',
          },
        });
        debugLog('[EmbedWidgetEditorPage] REST response status:', response.status);
        if (response.ok) {
          const result = await response.json();
          debugLog('[EmbedWidgetEditorPage] REST response:', result);
          // Handle various response formats:
          // - { handle: '...' }
          // - { data: { handle: '...' } }
          // - { data: [{ handle: '...' }] }  <-- API returns this format
          let handle = result?.handle || result?.Handle;
          if (!handle && result?.data) {
            if (Array.isArray(result.data) && result.data.length > 0) {
              handle = result.data[0]?.handle || result.data[0]?.Handle;
            } else {
              handle = result.data?.handle || result.data?.Handle;
            }
          }
          if (handle) {
            debugLog('[EmbedWidgetEditorPage] Got handle from REST API:', handle);
            setLocalHandle(handle);
          } else {
            console.warn('[EmbedWidgetEditorPage] No handle in REST response');
          }
        } else {
          console.warn('[EmbedWidgetEditorPage] REST API returned non-ok status');
        }
      } catch (error) {
        console.error('[EmbedWidgetEditorPage] REST profile fetch failed:', error);
      }
    };

    fetchHandle();
  }, [isAuthenticated, user?.id, user?.handle, jwt, appBaseUrl, fetchProfile, localHandle]);

  // Get user info - use user.handle first, then localHandle as fallback
  const userHandle = user?.handle || localHandle || '';
  const userId = user?.id || '';

  // Create stable fetch functions that use current jwt
  const fetchBrains = useCallback(async () => {
    debugLog('[EmbedWidgetEditorPage] fetchBrains called, jwt:', !!jwt, 'appBaseUrl:', appBaseUrl);
    if (!jwt || !appBaseUrl) {
      debugLog('[EmbedWidgetEditorPage] fetchBrains: Missing jwt or appBaseUrl, returning []');
      return [];
    }
    const result = await fetchUserBrainsApi(appBaseUrl, jwt);
    debugLog('[EmbedWidgetEditorPage] fetchBrains result:', result.length, 'brains');
    return result;
  }, [jwt, appBaseUrl]);

  // Get onConfigChange from config provider
  const { onConfigChange } = useEmbedConfig();

  // Use shared hook for all editor logic
  const { config, setConfig, brains, loading, copied, embedPath, embedCode, handleCopy } = useEmbedEditor({
    userHandle,
    userId,
    appBaseUrl: appBaseUrl || 'https://spaces.bldr.it',
    mediaBaseUrl: mediaBaseUrl || '',
    assetApiUrl: assetApiUrl || '',
    fetchBrains,
  });

  // Notify parent (WordPress) when config changes
  React.useEffect(() => {
    if (onConfigChange && !loading) {
      // Build full config object for WordPress
      const fullConfig = {
        ...config,
        id: userId,
        ownerHandle: userHandle,
        path: embedPath,
      };
      debugLog('[EmbedWidgetEditorPage] Sending config change to parent:', fullConfig);
      onConfigChange(fullConfig);
    }
  }, [config, userId, userHandle, embedPath, onConfigChange, loading]);

  // Debug logging for editor state
  debugLog('[EmbedWidgetEditorPage] Editor state:', {
    loading,
    brainsCount: brains.length,
    configType: config.type,
    brainId: config.brainId,
  });

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <EmbedLoginPage />;
  }

  // Show loading
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          backgroundColor: theme.colors.background.subtle,
        }}
      >
        <div
          style={{
            width: '32px',
            height: '32px',
            border: `3px solid ${theme.colors.border.default}`,
            borderTopColor: primaryColor,
            borderRadius: theme.borderRadius.full,
            animation: 'embed-spin 0.8s linear infinite',
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: theme.colors.background.subtle,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: `${theme.spacing['3']} ${theme.spacing['4']}`,
          backgroundColor: theme.colors.background.surface,
          borderBottom: `1px solid ${theme.colors.border.default}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h1
            style={{
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.semibold,
              color: theme.colors.text.primary,
              margin: 0,
            }}
          >
            Widget Editor
          </h1>
          <p
            style={{
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.text.secondary,
              margin: `${theme.spacing['1']} 0 0 0`,
            }}
          >
            Configure your embed widget
          </p>
        </div>
        <button
          onClick={() => logout()}
          style={{
            padding: `${theme.spacing['1.5']} ${theme.spacing['3']}`,
            backgroundColor: 'transparent',
            color: theme.colors.text.secondary,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: theme.borderRadius.lg,
            fontSize: theme.typography.fontSize.sm,
            cursor: 'pointer',
          }}
        >
          Sign out
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: theme.spacing['4'] }}>
        <EmbedConfigPanel
          config={config}
          onConfigChange={setConfig}
          brains={brains}
          userHandle={userHandle}
          userId={userId}
          loading={loading}
          embedCode={embedCode}
          onCopy={handleCopy}
          copied={copied}
          theme={theme}
          primaryColor={primaryColor}
          appendMediaUrl={(url) => (url ? `${mediaBaseUrl}${url}` : '')}
        />

        {/* Preview */}
        <div
          style={{
            marginTop: theme.spacing['4'],
            backgroundColor: theme.colors.background.surface,
            borderRadius: theme.borderRadius.xl,
            border: `1px solid ${theme.colors.border.default}`,
            padding: theme.spacing['4'],
          }}
        >
          <h2
            style={{
              fontSize: theme.typography.fontSize.base,
              fontWeight: theme.typography.fontWeight.semibold,
              color: theme.colors.text.primary,
              margin: `0 0 ${theme.spacing['4']} 0`,
            }}
          >
            Preview
          </h2>
          <div
            style={{
              border: `1px solid ${theme.colors.border.default}`,
              borderRadius: theme.borderRadius.lg,
              overflow: 'hidden',
              maxWidth: '100%',
              height: '300px',
              background: (() => {
                if (config.useCustomTheme) {
                  return config.customMode === 'dark' ? '#111827' : '#f9fafb';
                }
                const preset = THEME_PRESETS.find((p) => p.id === config.themePreset);
                return preset?.mode === 'dark' ? '#111827' : '#f9fafb';
              })(),
            }}
          >
            {embedPath ? (
              <iframe
                key={`${embedPath}-${config.themePreset}-${config.useCustomTheme}-${config.customMode}-${config.primaryColor}`}
                srcDoc={`<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>* { margin: 0; padding: 0; box-sizing: border-box; } html, body { width: 100%; height: 100%; }</style>
</head>
<body>
  ${embedCode.replace(
    /style="display: block; width: [^"]+; height: [^"]+;"/,
    'style="display: block; width: 100%; height: 100%;"',
  )}
</body>
</html>`}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title="Embed Preview"
              />
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: theme.colors.text.tertiary,
                }}
              >
                <p style={{ textAlign: 'center', padding: theme.spacing['4'] }}>
                  {config.type === 'brain' ? 'Select a brain to preview' : 'Configure your widget to preview'}
                </p>
              </div>
            )}
          </div>
          <p
            style={{
              fontSize: theme.typography.fontSize.xs,
              color: theme.colors.text.tertiary,
              textAlign: 'center',
              marginTop: theme.spacing['3'],
            }}
          >
            Preview is scaled to fit. Actual embed will use configured dimensions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmbedWidgetEditorPage;
